import React, { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import axiosInstance from 'shared/services/api/axiosInstance';
import { useTeam } from 'shared/context/teamContext';
import BrandLoader from 'shared/components/ui/BrandLoader';
import { Section, neutralBtn } from 'features/settings/components/SettingsRows';
import GoogleMark from 'shared/components/ui/GoogleMark';

const publicAsset = (p) => `${(process.env.PUBLIC_URL || '').replace(/\/?$/, '')}${p}`;

/**
 * Settings → Personal → Connected accounts.
 * Sign in with Slack maps this Trodo user to a Slack user id (identity only).
 */
export default function ConnectedAccountsPanel() {
  const { selectedTeam } = useTeam();
  const location = useLocation();
  const teamId = selectedTeam?.id || localStorage.getItem('selectedTeam');
  const [status, setStatus] = useState(null);
  const [me, setMe] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!teamId) return;
    try {
      const { data } = await axiosInstance.get('/integrations/slack/status', {
        params: { team_id: teamId },
      });
      setStatus(data);
      setError('');
    } catch (err) {
      setStatus(null);
      setError(err?.response?.data?.error || err.message || 'Failed to load Slack status');
    }
  }, [teamId]);

  useEffect(() => { load(); }, [load]);

  // Who you are signed in as — the Google row below is read from this.
  useEffect(() => {
    let cancelled = false;
    axiosInstance.get('/auth/me')
      .then(({ data }) => { if (!cancelled) setMe(data?.user || null); })
      .catch(() => { if (!cancelled) setMe(null); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('slack_user') === 'connected') load();
    if (params.get('slack_user') === 'error') {
      setError(params.get('reason') || 'Slack connection failed');
    }
  }, [location.search, load]);

  const connect = async () => {
    setBusy(true);
    setError('');
    try {
      const { data } = await axiosInstance.get('/integrations/slack/connect', {
        params: { team_id: teamId, return_to: location.pathname },
      });
      if (data?.url) window.location.href = data.url;
      else setError('Slack connect URL missing');
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Failed to start Slack connect');
    } finally {
      setBusy(false);
    }
  };

  const disconnect = async () => {
    setBusy(true);
    setError('');
    try {
      await axiosInstance.delete('/integrations/slack/connection', {
        params: { team_id: teamId },
      });
      await load();
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Failed to disconnect');
    } finally {
      setBusy(false);
    }
  };

  const connected = !!status?.connected;

  const slackStatus = status?.needs_reconnect
    ? `Needs reconnecting in ${status.source === 'organization' ? 'Organization Slack' : 'Integrations'}`
    : connected
      ? `${status.slack_team_name || status.slack_team_id}${status.slack_user_name ? ` · ${status.slack_user_name}` : ''}`
      : !status?.org_connected
        ? 'Connect Slack for this organization first'
        : 'Not connected';

  const rowCls = 'flex items-center gap-3 border-b border-[color:var(--border-subtle)] py-3';

  return (
    <div className="w-full text-left" style={{ fontFamily: 'Inter, sans-serif' }}>
      <Section title="Connected accounts">
        {/* Google is identity, not a connection: for anyone who signed up with
            it, it is the only way in, so there is no Disconnect here. */}
        {me?.isGoogleUser ? (
          <div className={rowCls}>
            <GoogleMark size={18} className="shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="text-[13px] text-[color:var(--text-primary)]">Google</p>
              <p className="truncate text-[12px] text-[color:var(--text-muted)]">{me.email}</p>
            </div>
            <span className="text-[12px] text-[color:var(--text-muted)]">Signed in</span>
          </div>
        ) : null}

        <div className={rowCls}>
          <img
            src={publicAsset('/icons/slack.png')}
            alt=""
            aria-hidden
            className="h-[18px] w-[18px] shrink-0 object-contain"
            loading="lazy"
          />
          <div className="min-w-0 flex-1">
            <p className="text-[13px] text-[color:var(--text-primary)]">Slack</p>
            <p
              className={`truncate text-[12px] ${
                status?.needs_reconnect
                  ? 'text-[color:var(--feedback-warning)]'
                  : 'text-[color:var(--text-muted)]'
              }`}
            >
              {slackStatus}
            </p>
          </div>
          {connected ? (
            <button type="button" onClick={disconnect} disabled={busy} className={neutralBtn}>
              {busy ? <BrandLoader size="sm" label={null} /> : null}
              Disconnect
            </button>
          ) : (
            <button
              type="button"
              onClick={connect}
              disabled={busy || !status?.org_connected}
              className={neutralBtn}
            >
              {busy ? <BrandLoader size="sm" label={null} /> : null}
              Connect
            </button>
          )}
        </div>
      </Section>
      {error ? <p className="text-[12px] text-[color:var(--feedback-error)]">{error}</p> : null}
    </div>
  );
}
